import React, { useEffect, useState } from 'react';
import {
    Box, Typography, ToggleButton, ToggleButtonGroup,
    TextField, MenuItem, Button, Alert, CircularProgress, Paper
} from '@mui/material';
import { TaxService } from '../../services/tax.service';
import { ITaxSettings, TaxMode } from '../../interfaces/tax.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

const MONTHS = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const TaxSettings: React.FC = () => {
    const { showSuccess, showError, showApiError } = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [settings, setSettings] = useState<ITaxSettings>({
        institutionId, taxMode: 'SLAB', taxYearStartMonth: 1
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        TaxService.getTaxSettings(institutionId)
            .then(s => setSettings(s))
            .catch((error) => {
                // 404 = settings never saved for this institution: the defaults are correct.
                if (error?.response?.status !== 404) showApiError(error, 'Failed to load tax settings');
            })
            .finally(() => setLoading(false));
    }, [institutionId]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const saved = await TaxService.upsertTaxSettings({ ...settings, institutionId });
            setSettings(saved);
            showSuccess('Tax settings saved');
        } catch {
            showError('Failed to save tax settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box data-help-page="tax.settings.page" p={3}>
            <Typography variant="h5" mb={3}>Tax Settings</Typography>
            <Paper data-help-section="tax.settings.section.mode" sx={{ p: 3, maxWidth: 500 }}>
                <Typography variant="subtitle2" mb={1}>Tax Calculation Mode</Typography>
                <ToggleButtonGroup data-help="tax.settings.field.taxMode"
                    exclusive
                    value={settings.taxMode}
                    onChange={(_, v) => v && setSettings(s => ({ ...s, taxMode: v as TaxMode }))}
                    sx={{ mb: 3 }}
                >
                    <ToggleButton value="SLAB">Slab-Based</ToggleButton>
                    <ToggleButton value="FLAT">Flat Rate</ToggleButton>
                </ToggleButtonGroup>

                {settings.taxMode === 'FLAT' && (
                    <TextField data-help="tax.settings.field.flatRate"
                        label="Flat Rate (%)"
                        type="number"
                        value={settings.flatRate ?? ''}
                        onChange={e => setSettings(s => ({ ...s, flatRate: parseFloat(e.target.value) }))}
                        fullWidth sx={{ mb: 3 }}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                )}

                <TextField data-help="tax.settings.field.startMonth"
                    select label="Tax Year Start Month"
                    value={settings.taxYearStartMonth}
                    onChange={e => setSettings(s => ({ ...s, taxYearStartMonth: parseInt(e.target.value) }))}
                    fullWidth sx={{ mb: 3 }}
                >
                    {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </TextField>

                <Alert severity="info" sx={{ mb: 2 }}>
                    Slab-based is recommended. Switch to Tax Slabs to configure tax brackets.
                </Alert>

                <Button data-help="tax.settings.save" variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Settings'}
                </Button>
            </Paper>
        </Box>
    );
};

export default TaxSettings;
